import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { format, parseISO, addDays, isSameDay, startOfDay } from "date-fns";
import { nl } from "date-fns/locale";
import { CalendarDays, Clock, UserRound, CheckSquare, Circle, ChevronRight, ChevronLeft, Sparkles } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Popover, PopoverTrigger, PopoverContent } from "../../components/ui/popover";
import { Calendar } from "../../components/ui/calendar";

const loadFromDur = (m) => (m <= 20 ? 1 : m <= 40 ? 2 : m <= 60 ? 3 : m <= 90 ? 4 : 5);

export default function TodayPlanner() {
  const navigate = useNavigate();
  const [appts, setAppts] = useState([]);
  const [privates, setPrivates] = useState([]);
  const [todos, setTodos] = useState([]);
  const [selDate, setSelDate] = useState(() => startOfDay(new Date()));

  const selStr = format(selDate, "yyyy-MM-dd");
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 30000); return () => clearInterval(t); }, []);
  const isTodaySel = isSameDay(selDate, now);
  const relLabel = isTodaySel ? "Vandaag"
    : isSameDay(selDate, addDays(now, 1)) ? "Morgen"
    : isSameDay(selDate, addDays(now, -1)) ? "Gisteren"
    : format(selDate, "EEEE", { locale: nl });

  const load = useCallback(() => {
    const start = `${selStr}T00:00:00`;
    const end = `${selStr}T23:59:59`;
    api.get("/admin/appointments", { params: { start, end } })
      .then((r) => setAppts((r.data || []).filter((a) => a.status !== "cancelled")))
      .catch(() => {});
    api.get("/admin/blocks", { params: { start: selStr, end: selStr } })
      .then((r) => setPrivates((r.data || []).filter((b) => b.kind === "private")))
      .catch(() => {});
    api.get("/admin/todos", { params: { include_done: false } })
      .then((r) => setTodos((r.data || []).filter((t) =>
        t.due_date === selStr || (t.scheduled_start || "").startsWith(selStr))))
      .catch(() => {});
  }, [selStr]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("planner:refresh", h);
    return () => window.removeEventListener("planner:refresh", h);
  }, [load]);

  const tickTodo = async (t) => {
    await api.put(`/admin/todos/${t.id}`, { done: true });
    toast.success("To-do afgerond");
    load();
  };

  const agenda = [
    ...appts.map((a) => ({ key: `a-${a.id}`, type: "appt", start: a.start,
      title: a.customer_name, sub: a.treatment_name, color: "#D9A5A5", raw: a })),
    ...privates.map((b) => ({ key: `p-${b.id}`, type: "private", start: `${b.date}T${b.start || "00:00"}:00`,
      title: b.title || "Privé", sub: "Privé afspraak", color: b.color || "#93B5C6" })),
  ].sort((x, y) => (x.start || "").localeCompare(y.start || ""));

  const hm = (iso) => { try { return format(parseISO(iso), "HH:mm"); } catch { return ""; } };

  const loadOf = (t) => (t.load || 0) || loadFromDur(t.duration_min || 30);
  const looseTodos = todos.filter((t) => !t.scheduled_start && !t.due_time);
  const timedTodos = todos.filter((t) => t.scheduled_start || t.due_time);

  const nextItem = isTodaySel ? agenda.find((it) => { try { return parseISO(it.start) > now; } catch { return false; } }) : null;
  const minsUntil = nextItem ? Math.round((parseISO(nextItem.start) - now) / 60000) : 0;
  const untilLabel = minsUntil <= 0 ? "nu"
    : minsUntil < 60 ? `over ${minsUntil} min`
    : `over ${Math.floor(minsUntil / 60)} u${minsUntil % 60 ? ` ${minsUntil % 60} min` : ""}`;

  return (
    <div className="max-w-2xl" data-testid="planner-today">
      <div className="flex items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-medium tracking-tight capitalize" data-testid="planner-day-label">{relLabel}</h1>
          <p className="text-ink-muted text-sm capitalize">{format(selDate, "EEEE d MMMM yyyy", { locale: nl })}</p>
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <div className="flex items-center gap-1">
          <button onClick={() => setSelDate((d) => addDays(d, -1))} data-testid="today-prev-day" aria-label="Dag terug"
            className="w-10 h-10 rounded-full border border-[#E8E2D9] bg-white flex items-center justify-center text-ink-muted hover:text-brand hover:border-brand/40 transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <Popover>
            <PopoverTrigger asChild>
              <button data-testid="today-pick-date" aria-label="Kies een datum"
                className="w-10 h-10 rounded-full border border-[#E8E2D9] bg-white flex items-center justify-center text-ink-muted hover:text-brand hover:border-brand/40 transition-colors">
                <CalendarDays className="w-5 h-5" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar mode="single" selected={selDate} onSelect={(d) => d && setSelDate(startOfDay(d))} locale={nl} />
            </PopoverContent>
          </Popover>
          {!isTodaySel && (
            <button onClick={() => setSelDate(startOfDay(new Date()))} data-testid="today-reset"
              className="h-10 px-4 rounded-full border border-[#E8E2D9] bg-white text-sm text-brand hover:border-brand/40 transition-colors">Vandaag</button>
          )}
          <button onClick={() => setSelDate((d) => addDays(d, 1))} data-testid="today-next-day" aria-label="Dag vooruit"
            className="w-10 h-10 rounded-full border border-[#E8E2D9] bg-white flex items-center justify-center text-ink-muted hover:text-brand hover:border-brand/40 transition-colors">
            <ChevronRight className="w-5 h-5" />
          </button>
          </div>
          <div className="flex items-center gap-3 text-xs text-ink-muted" data-testid="day-counts">
            <span className="inline-flex items-center gap-1" title="Afspraken deze dag"><CalendarDays className="w-3.5 h-3.5" /> {agenda.length}</span>
            <span className="inline-flex items-center gap-1" title="Taken deze dag"><CheckSquare className="w-3.5 h-3.5" /> {todos.length}</span>
          </div>
        </div>
      </div>

      <button onClick={() => navigate("/planner/agenda?assist=1")} data-testid="today-assistant-btn"
        className="mb-5 w-full inline-flex items-center justify-center gap-2 rounded-full py-2.5 text-sm border border-brand/30 bg-brand-light text-brand-hover hover:bg-brand hover:text-white transition-colors">
        <Sparkles className="w-4 h-4" /> Planningsassistent
      </button>

      {/* Eerstvolgende afspraak uitgelicht */}
      {nextItem && (() => {
        const soon = minsUntil >= 0 && minsUntil <= 15;
        return (
        <button type="button" data-testid="next-appt" data-soon={soon ? "1" : "0"} onClick={() => navigate(`/planner/agenda?focus=${encodeURIComponent(nextItem.start)}`)}
          className={`w-full text-left mb-5 rounded-2xl bg-brand text-white p-5 transition-all active:scale-[0.99] ${soon ? "next-appt-soon ring-2 ring-amber-300 shadow-[0_0_0_4px_rgba(252,211,77,0.25),0_12px_34px_rgb(74,63,63,0.22)]" : "shadow-[0_10px_30px_rgb(74,63,63,0.14)] hover:shadow-[0_12px_34px_rgb(74,63,63,0.2)]"}`}>
          <div className="flex items-center gap-2 text-white/85 text-xs uppercase tracking-wide mb-2">
            <Clock className="w-4 h-4" strokeWidth={2} /> {soon ? (minsUntil <= 0 ? "Begint nu" : `Begint over ${minsUntil} min`) : `Volgende afspraak · ${untilLabel}`}
          </div>
          <div className="flex items-center gap-4">
            <div className="text-3xl font-semibold tabular-nums shrink-0" data-testid="next-appt-time">{hm(nextItem.start)}</div>
            <span className="w-1 self-stretch rounded-full bg-white/40 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-lg font-medium truncate" data-testid="next-appt-title">{nextItem.title}</div>
              <div className="text-white/80 text-sm truncate flex items-center gap-1">
                {nextItem.type === "private" ? <UserRound className="w-3.5 h-3.5" /> : null}{nextItem.sub}
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-white/70 shrink-0" />
          </div>
        </button>
        );
      })()}

      {/* Hele dag: losse taken zonder vaste tijd */}
      {looseTodos.length > 0 && (
        <div className="bg-white rounded-2xl border border-[#E8E2D9] p-5 mb-5 shadow-[0_8px_30px_rgb(74,63,63,0.04)]" data-testid="planner-allday">
          <div className="flex items-center gap-2 mb-3">
            <CheckSquare className="w-5 h-5 text-brand" strokeWidth={1.5} />
            <h2 className="text-lg font-medium">Hele dag</h2>
            <span className="text-xs text-ink-muted">losse taken · geen vaste tijd</span>
          </div>
          <div className="space-y-1">
            {looseTodos.map((t) => (
              <div key={t.id} data-testid={`planner-allday-${t.id}`} className="flex items-center gap-3 py-2">
                <button onClick={() => tickTodo(t)} data-testid={`planner-allday-tick-${t.id}`} className="shrink-0 text-ink-muted hover:text-brand" title="Afvinken"><Circle className="w-5 h-5" /></button>
                <span className="w-1.5 self-stretch rounded-full shrink-0" style={{ backgroundColor: t.color }} />
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{t.title}</div>
                  {t.notes && <div className="text-sm text-ink-muted truncate">{t.notes}</div>}
                </div>
                <span className="text-[11px] text-ink-muted shrink-0 rounded-full bg-cream-subtle px-2 py-0.5" title="Belasting (telt mee in de dagbelasting)">bel. {loadOf(t)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Agenda van vandaag */}
      <div className="bg-white rounded-2xl border border-[#E8E2D9] p-5 mb-5 shadow-[0_8px_30px_rgb(74,63,63,0.04)]">
        <div className="flex items-center gap-2 mb-4">
          <CalendarDays className="w-5 h-5 text-brand" strokeWidth={1.5} />
          <h2 className="text-lg font-medium">Afspraken</h2>
          <button onClick={() => navigate("/planner/agenda")} data-testid="today-to-agenda"
            className="ml-auto text-sm text-brand hover:underline flex items-center gap-0.5">Agenda <ChevronRight className="w-4 h-4" /></button>
        </div>
        {!agenda.length && <p className="text-ink-muted text-sm">Geen afspraken op deze dag.</p>}
        <div className="divide-y divide-[#F2EBE1]">
          {agenda.map((it) => (
            <div key={it.key} data-testid={`today-item-${it.key}`} className="flex items-center gap-3 py-3">
              <div className="text-sm font-medium tabular-nums w-12 shrink-0">{hm(it.start)}</div>
              <span className="w-1.5 self-stretch rounded-full shrink-0" style={{ backgroundColor: it.color }} />
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate">{it.title}</div>
                <div className="text-sm text-ink-muted truncate flex items-center gap-1">
                  {it.type === "private" ? <UserRound className="w-3 h-3" /> : null}{it.sub}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* To-do's van vandaag */}
      <div className="bg-white rounded-2xl border border-[#E8E2D9] p-5 shadow-[0_8px_30px_rgb(74,63,63,0.04)]">
        <div className="flex items-center gap-2 mb-4">
          <CheckSquare className="w-5 h-5 text-brand" strokeWidth={1.5} />
          <h2 className="text-lg font-medium">To-do's</h2>
          <button onClick={() => navigate("/planner/todos")} data-testid="today-to-todos"
            className="ml-auto text-sm text-brand hover:underline flex items-center gap-0.5">Alle <ChevronRight className="w-4 h-4" /></button>
        </div>
        {!timedTodos.length && <p className="text-ink-muted text-sm">Geen to-do's met een vaste tijd voor deze dag. 🌿</p>}
        <div className="space-y-1">
          {timedTodos.map((t) => (
            <div key={t.id} data-testid={`today-todo-${t.id}`} className="flex items-center gap-3 py-2">
              <button onClick={() => tickTodo(t)} data-testid={`today-todo-tick-${t.id}`} className="shrink-0 text-ink-muted hover:text-brand" title="Afvinken">
                <Circle className="w-5 h-5" />
              </button>
              <span className="w-1.5 self-stretch rounded-full shrink-0" style={{ backgroundColor: t.color }} />
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate">{t.title}</div>
                {t.notes && <div className="text-sm text-ink-muted truncate">{t.notes}</div>}
              </div>
              {t.due_time && <div className="text-xs text-ink-muted shrink-0 inline-flex items-center gap-1"><Clock className="w-3 h-3" />{t.due_time}</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
